import _ from 'lodash'
import { createSelector } from 'reselect'
import { Statsig } from 'statsig-react'
import produce from 'immer'
import { isDeveloperEmail } from '@feedyou/utils'

import { RootState } from '../../reducers'
import { FeatureAvailabilities, FeatureName, FeatureValues } from './types'
import { getEnvFeatures } from './featuresService.window'
import { getTariffFeatures } from './featuresSchemaService'
import { expandTariffFeatures, getAdminFeatures, getAdminTariff } from './featuresService.general'
import {
  UninitializedAvailabilitiesException,
  UninitializedValuesException
} from '../../features/statsig/utils'
import { FeatureValueSet } from './featuresSlice'
import { emitException } from '../../lib/exceptions'

export function featuresSliceSelector(state: RootState) {
  return state.features
}

export const featureValuesSelector = (set: FeatureValueSet) => (state: RootState) => {
  return featuresSliceSelector(state).values[set]
}

export function areFeatureValuesReadySelector(state: RootState) {
  return featuresSliceSelector(state).areValuesReady
}

export function isStatsigApiReadySelector(state: RootState) {
  return featuresSliceSelector(state).isStatsigApiReady
}

// Please think twice before using this outside of Diagnostics :)
export const featureValuesHierarchySelector = createSelector(
  (state: RootState) => state.user?.info?.email,
  featureValuesSelector('userSettings'),
  featureValuesSelector('statsig'),
  featureValuesSelector('devForcedFeatures'),
  areFeatureValuesReadySelector,
  (email, settingsFeatures, gateFeatures, devForcedFeatures, isInitialized) => {
    if (!isInitialized) {
      emitException(UninitializedValuesException)
    }

    const tariffFeatures = getTariffFeatures()
    const envFeatures = getEnvFeatures()
    const adminTariff = getAdminTariff(email)
    const adminFeatures = getAdminFeatures(email)

    // statsig returns false for unknown features so we need to filter them out
    const enabledGateFeatures = _.pickBy(gateFeatures, _.identity)

    // features expanded from beta tariff should not be overriden by env features
    const betaFeaturesExpanded = expandTariffFeatures(settingsFeatures, envFeatures)
    const adminFeaturesFromTariff = expandTariffFeatures(adminTariff)

    // some env features should overwrite tariff features when set
    const forcedEnvFeatures = envFeatures.SHARE_TOKEN_MAX_ROLE
      ? { SHARE_TOKEN_MAX_ROLE: envFeatures.SHARE_TOKEN_MAX_ROLE }
      : {}

    return [
      { name: 'tariffFeatures', values: tariffFeatures },
      { name: 'envFeatures', values: envFeatures },
      { name: 'enabledGateFeatures', values: enabledGateFeatures },
      { name: 'adminFeaturesFromTariff', values: adminFeaturesFromTariff },
      { name: 'betaFeaturesExpanded', values: betaFeaturesExpanded },
      { name: 'adminFeatures', values: adminFeatures },
      { name: 'forcedEnvFeatures', values: forcedEnvFeatures },
      { name: 'devForcedFeatures', values: isDeveloperEmail(email) ? devForcedFeatures : {} }
    ] as const
  }
)

export type FeatureValuesHierarchy = ReturnType<typeof featureValuesHierarchySelector>

/**
 * @derprecated
 * This selector is not exported for a reason! This is because we want to call
 * Statsig.manuallyLogGateExposure for each feature that is accessed,
 * which would be impossible to enforce if we exported this selector.
 * Please use featureValueSelector whenever possible
 */
const featuresValuesSelector = createSelector(
  featureValuesHierarchySelector, // I <3 PRETTIER
  categories => Object.assign({}, ...categories.map(category => category.values))
)

export function featureValueSelector<Name extends FeatureName>(featureName: Name) {
  return function featureValueSelector(state: RootState): FeatureValues[Name] {
    const result = featuresValuesSelector(state)[featureName]

    if (import.meta.env.MODE === 'production' && isStatsigApiReadySelector(state)) {
      // Manually logging AFTER features are accessed - this causes
      // UninitializedValuesException to be thrown before more cryptic
      // internal Statsig exception if statsig is not yet initialized :D
      Statsig.manuallyLogGateExposure(featureName.toLowerCase())
    }

    return result
  }
}

export function featureAvailabilitiesSelector(state: RootState): FeatureAvailabilities {
  const availabilities = featuresSliceSelector(state).availabilities

  if (availabilities === null || !areFeatureValuesReadySelector(state)) {
    emitException(UninitializedAvailabilitiesException)
    return {} as FeatureAvailabilities
  }

  return availabilities
}

export function featureAvailabilitySelector(state: RootState, featureName: FeatureName) {
  const availabilities = featureAvailabilitiesSelector(state)
  return availabilities?.[featureName]
}

export function featureDefaultsSelector(state: RootState) {
  const stateWithoutSettings = produce(state, draft => {
    draft.features.values.userSettings = {}
  })

  return featuresValuesSelector(stateWithoutSettings)
}

export function defaultFeatureValueSelector(name: FeatureName) {
  return function defaultFeatureValueSelector(state: RootState) {
    return featureDefaultsSelector(state)[name]
  }
}
